// ============================================================================
// KLINISOL CRO - Lesson Viewer Component (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// UPDATED: Added hyperlink support with target="_blank" and rel attributes
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { Printer, CheckCircle, Info } from 'lucide-react';

const LessonViewer = ({ content, contentFormat, onComplete, isCompleted }) => {
    const [timeOnPage, setTimeOnPage] = useState(0);
    const [scrollProgress, setScrollProgress] = useState(0);
    const contentRef = useRef(null);
    
    // Track time spent on page
    useEffect(() => {
        const timer = setInterval(() => {
            setTimeOnPage(prev => prev + 1);
        }, 1000);
        
        return () => clearInterval(timer);
    }, []);
    
    // Track scroll progress
    useEffect(() => {
        const handleScroll = () => {
            if (contentRef.current) {
                const element = contentRef.current;
                const scrollTop = element.scrollTop;
                const scrollHeight = element.scrollHeight - element.clientHeight;
                const progress = (scrollTop / scrollHeight) * 100;
                setScrollProgress(Math.min(100, Math.max(0, progress)));
            }
        };
        
        const element = contentRef.current;
        if (element) {
            element.addEventListener('scroll', handleScroll);
            return () => element.removeEventListener('scroll', handleScroll);
        }
    }, []);
    
    // Sanitize HTML content with hyperlink support
    const getSanitizedContent = () => {
        if (contentFormat === 'html') {
            return DOMPurify.sanitize(content, {
                ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 
                              'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'table', 'thead', 
                              'tbody', 'tr', 'th', 'td', 'div', 'span', 'a', 'img'],
                ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id', 'style', 'target', 'rel'],
                ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
            });
        } else if (contentFormat === 'markdown') {
            return content;
        } else {
            return content;
        }
    };
    
    // Format time display
    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };
    
    // Handle print
    const handlePrint = () => {
        window.print();
    };
    
    // Handle complete
    const handleComplete = () => {
        if (scrollProgress < 80) {
            const confirmComplete = window.confirm(
                'You have not scrolled through all the content. Are you sure you want to mark this as complete?'
            );
            if (!confirmComplete) return;
        }
        
        onComplete();
    };

    return (
        <div className="flex flex-col h-full bg-white rounded-2xl overflow-hidden shadow-md">
            {/* Progress Bar */}
            <div className="h-1.5 bg-gray-200 relative">
                <div 
                    className="h-full bg-gradient-to-r from-[#475569] to-[#334155] transition-all duration-300 ease-out"
                    style={{ width: `${scrollProgress}%` }}
                />
            </div>
            
            {/* Header */}
            <div className="flex justify-between items-center px-6 py-5 bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] border-b-2 border-gray-200">
                <div className="flex items-center gap-4">
                    {/* Timer */}
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl border-2 border-[#475569]">
                        <div className="w-2 h-2 bg-[#475569] rounded-full animate-pulse" />
                        <span className="text-sm font-bold text-[#475569] font-mono tracking-wide">
                            {formatTime(timeOnPage)}
                        </span>
                    </div>
                    
                    {/* Scroll Progress */}
                    <div className="text-sm text-gray-600 font-medium">
                        Reading Progress: <strong className="text-[#475569]">{Math.round(scrollProgress)}%</strong>
                    </div>
                </div>
                
                {/* Print Button */}
                <button 
                    onClick={handlePrint}
                    className="flex items-center gap-2 px-5 py-2.5 bg-white text-gray-600 border-2 border-gray-200 rounded-xl text-sm font-semibold hover:bg-gradient-to-br hover:from-[#f8fafc] hover:to-[#f1f5f9] hover:border-[#475569] hover:text-[#475569] hover:-translate-y-0.5 transition-all"
                    title="Print Lesson"
                >
                    <Printer className="w-4 h-4" />
                    <span>Print</span>
                </button>
            </div>
            
            {/* Content Area - Added lesson-content class for hyperlink styling */}
            <div 
                ref={contentRef}
                className="lesson-content flex-1 overflow-auto px-10 py-10 text-base leading-relaxed text-gray-900"
                dangerouslySetInnerHTML={{ __html: getSanitizedContent() }}
            />
            
            {/* Footer Actions */}
            <div className="px-6 py-6 bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] border-t-2 border-gray-200 flex justify-between items-center flex-wrap gap-4">
                <div className="flex items-center gap-4 flex-1">
                    {timeOnPage < 10 && !isCompleted && (
                        <div className="flex items-center gap-2 px-4 py-3 bg-gradient-to-br from-yellow-100 to-yellow-50 rounded-xl border-2 border-yellow-500 text-sm text-yellow-900 font-semibold">
                            <Info className="w-5 h-5" />
                            <span>Please spend at least {10 - timeOnPage} more second{10 - timeOnPage !== 1 ? 's' : ''} reviewing</span>
                        </div>
                    )}
                </div>
                
                {isCompleted ? (
                    <div className="flex items-center gap-3 px-6 py-3.5 bg-gradient-to-br from-green-100 to-green-50 rounded-xl border-2 border-green-500 shadow-lg">
                        <div className="w-7 h-7 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                            <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-sm font-bold text-green-700">
                            Completed
                        </span>
                    </div>
                ) : (
                    <button 
                        onClick={handleComplete}
                        disabled={timeOnPage < 10}
                        className={`flex items-center gap-2 px-8 py-3.5 rounded-xl text-base font-bold transition-all ${
                            timeOnPage < 10
                                ? 'bg-gray-400 text-white cursor-not-allowed opacity-60'
                                : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:-translate-y-0.5 hover:shadow-lg shadow-md'
                        }`}
                        title={timeOnPage < 10 ? 'Please spend at least 10 seconds reviewing this content' : 'Mark this lesson as complete'}
                    >
                        <CheckCircle className="w-5 h-5" />
                        <span>Mark as Complete</span>
                    </button>
                )}
            </div>
        </div>
    );
};

export default LessonViewer;